import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { AzzeraFiltri, FilterBar, useFiltersMeta } from "@/components/Filters";
import { ComparisonChart } from "@/components/Charts";
import { eurShort, num } from "@/lib/format";

function Blocco({ g, aperta, onToggle }) {
  const p = g.progressivo || {};
  return (
    <Card className="p-0 overflow-hidden" data-testid={`mon-blocco-${g.id}`}>
      <button type="button" onClick={onToggle} data-testid={`mon-toggle-${g.id}`}
        className="w-full flex items-center gap-2 px-6 py-3 text-left text-white bg-liquidato
          hover:brightness-110 transition-all duration-200">
        <ChevronDown size={16} className={`transition-transform duration-200 ${aperta ? "" : "-rotate-90"}`} />
        <span className="uppercase text-xs tracking-[0.18em] font-semibold">{g.titolo}</span>
      </button>
      {aperta && (
        <>
          <div className="p-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <ComparisonChart testId={`mon-chart-${g.id}`} height={280} labelFormat="perc" coloreDalSegno
                title="Scostamento % del liquidato rispetto al budget · mese per mese"
                data={g.righe} keys={[{ key: "scostamento_perc", name: "% vs budget", color: "#16A34A" }]} />
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
                  <tr>
                    <th className="text-left px-2 py-1.5">Mese</th>
                    <th className="text-right px-2 py-1.5">Budget</th>
                    <th className="text-right px-2 py-1.5">Liquidato</th>
                    <th className="text-right px-2 py-1.5">Scost. %</th>
                  </tr>
                </thead>
                <tbody>
                  {g.righe.map((r, i) => (
                    <tr key={r.mese} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                      <td className="px-2 py-1 font-medium whitespace-nowrap">{r.label}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-500 whitespace-nowrap">{eurShort(r.budget)}</td>
                      <td className="px-2 py-1 text-right font-mono whitespace-nowrap">{eurShort(r.liquidato)}</td>
                      <td className={`px-2 py-1 text-right font-mono font-semibold whitespace-nowrap
                        ${r.scostamento_perc === null ? "text-slate-400"
                          : r.scostamento_perc >= 0 ? "text-emerald-600" : r.scostamento_perc >= -10 ? "text-amber-600" : "text-red-600"}`}>
                        {r.scostamento_perc === null ? "—" : `${num(r.scostamento_perc, 2)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {p.fino_al && (
            <div className="px-4 pb-4" data-testid={`mon-progressivo-${g.id}`}>
              <div className="rounded-lg border border-slate-300 bg-slate-100 p-4 flex flex-wrap items-center gap-x-10 gap-y-3">
                <div>
                  <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">
                    Progressivo al {p.fino_al.slice(0, 5)}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{g.titolo}</div>
                </div>
                <div>
                  <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">Budget</div>
                  <div className="font-mono text-lg text-slate-500">{eurShort(p.budget)}</div>
                </div>
                <div>
                  <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">Liquidato</div>
                  <div className="font-mono text-lg font-semibold">{eurShort(p.liquidato)}</div>
                </div>
                {g.budget_anno ? (
                  <div>
                    <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">Budget anno</div>
                    <div className="font-mono text-lg text-slate-500">{eurShort(g.budget_anno)}</div>
                  </div>
                ) : null}
                <div className="ml-auto text-right">
                  <div className="uppercase text-[10px] tracking-[0.18em] text-slate-500">Scostamento</div>
                  <div className={`font-mono text-2xl font-semibold
                    ${p.scostamento_perc === null ? "text-slate-400"
                      : p.scostamento_perc >= 0 ? "text-emerald-600" : p.scostamento_perc >= -10 ? "text-amber-600" : "text-red-600"}`}>
                    {p.scostamento_perc === null ? "—"
                      : `${p.scostamento_perc > 0 ? "+" : ""}${num(p.scostamento_perc, 2)}%`}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** Monitoraggio 2026: liquidato (totale, AUTO, NON AUTO) confrontato con il budget della banca. */
export default function Monitoraggio() {
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: 2026 });
  const [dealers, setDealers] = useState([]);
  const [d, setD] = useState(null);
  const [chiuse, setChiuse] = useState({});
  const [reset, setReset] = useState(0);

  useEffect(() => {
    api.get(`/dashboard/monitoraggio?${qs(filters)}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [filters]);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  return (
    <div data-testid="monitoraggio-page">
      <SectionTitle sub={`Liquidato ${filters.anno} confrontato con il budget assegnato dalla banca · totale, AUTO e NON AUTO`}
        right={
          <a href={`/api/report/monitoraggio.pdf?${qs(filters)}`} target="_blank" rel="noreferrer"
            data-testid="btn-report-monitoraggio-pdf"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
            Scarica report PDF
          </a>
        }>
        Monitoraggio 2026
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-3">
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={["anno", "commerciale", "settore", "dealer"]} />
        <AzzeraFiltri onClick={() => { setFilters({ anno: 2026 }); setReset((v) => v + 1); }} />
      </div>

      {!d ? (
        <div className="text-sm text-slate-500" data-testid="monitoraggio-loading">Caricamento…</div>
      ) : (
        <>
          <div className="space-y-6">
            {d.grafici.map((g) => (
              <Blocco key={g.id} g={g} aperta={!chiuse[g.id]}
                onToggle={() => setChiuse((v) => ({ ...v, [g.id]: !v[g.id] }))} />
            ))}
          </div>
          {d.budget_mesi_valorizzati < 12 && (
            <p className="text-xs text-slate-500 mt-4" data-testid="monitoraggio-nota-budget">
              Budget mensile valorizzato per {d.budget_mesi_valorizzati} mesi su 12
              (ottobre–dicembre non ancora ripartiti dalla banca). Budget annuo dichiarato:
              NON AUTO {eurShort(d.budget_anno?.pos)} · AUTO {eurShort(d.budget_anno?.auto)}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
